import {
	type OrderSampleAttachmentType,
	OrderSampleStatus,
	type SampleActionActor,
	type SampleActionType,
	type UpdateSampleStatusInput,
	useUpdateSampleStatusMutation,
} from '@codezo/graphql';
import { FileText, Trash2, Upload } from 'lucide-react';
import { useRef, useState } from 'react';
import { toast } from 'sonner';
import { useSampleAttachmentUpload } from '../../hooks/useSampleAttachmentUpload';
import { Button } from '../ui/button';
import {
	Dialog,
	DialogBody,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogForm,
	DialogHeader,
	DialogTitle,
} from '../ui/dialog';
import { Field } from '../ui/field';
import { Textarea } from '../ui/textarea';
import { SAMPLE_MEDIA_ACCEPT } from '../../lib/file-types';

/**
 * One move the client can make on a sample round, as the payload describes it.
 *
 * SET_PAYMENT_TREATMENT is admin-only and never reaches here, which is why
 * `status` being null is treated as an error rather than a second mode.
 */
export interface ClientSampleAction {
	action: SampleActionType;
	actor: SampleActionActor;
	label: string;
	status?: OrderSampleStatus | null;
	blocked?: boolean | null;
	blockedReason?: string | null;
	requiredFields: string[];
	optionalFields: string[];
	requiresAttachments?: OrderSampleAttachmentType | null;
	supportsAttachments?: OrderSampleAttachmentType | null;
	hasAttachments?: boolean | null;
}

/**
 * Approving or declining a sample round.
 *
 * ── Why it is its own component ──
 *
 * Both order pages ask the client the same two questions about the same round,
 * and the answer involves more than a mutation: a decline needs a reason and
 * the reason is mandatory, an action can require attachments, and the files
 * have to be uploaded and confirmed BEFORE the status moves or the transition
 * is rejected for having none. That sequence lived inside `SampleCard`, so the
 * v2 flow could either grow a second copy of it or call this.
 *
 * Nothing here decides what the client may do — `availableActions` does, and a
 * blocked action never gets this far.
 */
export function ClientSampleActionModal({
	action,
	sampleId,
	productName,
	onClose,
	onSuccess,
}: {
	action: ClientSampleAction;
	sampleId: string;
	productName: string;
	onClose: () => void;
	onSuccess: () => void;
}) {
	const [notes, setNotes] = useState('');
	const [updateStatus, { loading }] = useUpdateSampleStatusMutation();
	const fileInputRef = useRef<HTMLInputElement>(null);
	const upload = useSampleAttachmentUpload();

	const rejecting = action.status === OrderSampleStatus.Rejected;
	const attachmentType = action.requiresAttachments || action.supportsAttachments || null;
	const needsAttachments = !!action.requiresAttachments && !action.hasAttachments;
	const canAttach = !!attachmentType;

	const close = () => {
		upload.reset();
		onClose();
	};

	const submit = async () => {
		if (needsAttachments && upload.pendingFiles.length === 0) {
			toast.error('Please upload at least one file');
			return;
		}
		// Files first: the transition checks for them server-side, so uploading
		// afterwards would fail the very move it is meant to support.
		if (canAttach && upload.pendingFiles.length > 0) {
			const ok = await upload.uploadAll(sampleId, attachmentType!);
			if (!ok) return;
		}
		if (!action.status) {
			toast.error('This action is missing a target status');
			return;
		}

		const input: UpdateSampleStatusInput = { status: action.status };
		if (action.status === OrderSampleStatus.Approved) {
			input.approvalNotes = notes.trim() || undefined;
		} else if (rejecting) {
			if (!notes.trim()) {
				toast.error('Please provide a reason for rejection');
				return;
			}
			input.rejectionReason = notes.trim();
			input.requestNewSample = true;
		}

		try {
			const { data } = await updateStatus({ variables: { id: sampleId, input } });
			if (data?.updateSampleStatus?.success) {
				toast.success(action.label);
				upload.reset();
				onSuccess();
			} else {
				toast.error(data?.updateSampleStatus?.message || 'Failed to update sample');
			}
		} catch {
			toast.error('Failed to update sample');
		}
	};

	return (
		<Dialog
			open
			onOpenChange={(open) => {
				if (!open) close();
			}}
		>
			<DialogContent className="sm:max-w-md">
				<DialogHeader>
					<DialogTitle>{action.label}</DialogTitle>
					<DialogDescription>
						{rejecting
							? `Reject the sample for "${productName}". A new sample round will be requested.`
							: `Approve the sample for "${productName}". Production will proceed.`}
					</DialogDescription>
				</DialogHeader>
				<DialogForm onSubmit={submit}>
					<DialogBody>
						<div className="flex flex-col gap-4">
							<Field
								label={rejecting ? 'Rejection reason' : 'Notes (optional)'}
								required={rejecting}
							>
								<Textarea
									value={notes}
									onChange={(e) => setNotes(e.target.value)}
									placeholder={
										rejecting
											? 'Please explain why you are rejecting this sample…'
											: 'Any notes for the team (optional)…'
									}
									rows={3}
									autoFocus
								/>
							</Field>

							{canAttach && (
								<div>
									<div className="mb-1.5 text-sm font-medium text-text-1">
										Reference files{' '}
										{needsAttachments && (
											<span className="text-destructive">*</span>
										)}
									</div>
									<div
										onClick={() => fileInputRef.current?.click()}
										className="flex cursor-pointer flex-col items-center gap-2 rounded-lg border border-dashed border-line-2 px-4 py-5 text-center transition-colors hover:border-line-3 hover:bg-surface-hover"
									>
										<Upload size={16} className="text-text-4" />
										<div className="text-sm text-text-3">
											{upload.converting
												? 'Converting…'
												: 'Click to upload files'}
										</div>
										<div className="text-xs text-text-4">
											Images, videos, PDFs
										</div>
										<input
											ref={fileInputRef}
											type="file"
											multiple
											accept={SAMPLE_MEDIA_ACCEPT}
											className="hidden"
											onChange={(e) => {
												if (e.target.files) upload.addFiles(e.target.files);
												e.target.value = '';
											}}
										/>
									</div>

									{upload.pendingFiles.length > 0 && (
										<div className="mt-2 flex flex-col gap-1">
											{upload.pendingFiles.map((f, i) => (
												<div
													key={i}
													className="flex items-center gap-2 rounded-md bg-surface-fill px-2.5 py-1.5 text-xs"
												>
													{f.preview ? (
														<img
															src={f.preview}
															alt=""
															className="h-7 w-7 shrink-0 rounded object-cover"
														/>
													) : (
														<FileText
															size={14}
															className="shrink-0 text-text-4"
														/>
													)}
													<span className="min-w-0 flex-1 truncate text-text-2">
														{f.file.name}
													</span>
													<span className="shrink-0 text-text-4">
														{(f.file.size / 1024).toFixed(0)} KB
													</span>
													<button
														type="button"
														onClick={() => upload.removeFile(i)}
														className="shrink-0 rounded p-0.5 text-text-4 transition-colors hover:text-text-1"
													>
														<Trash2 size={11} />
													</button>
												</div>
											))}
										</div>
									)}
								</div>
							)}
						</div>
					</DialogBody>
					<DialogFooter>
						<Button
							type="button"
							variant="outline"
							onClick={close}
							disabled={loading || upload.uploading}
						>
							Cancel
						</Button>
						<Button
							type="submit"
							variant={rejecting ? 'destructive' : 'default'}
							disabled={
								loading ||
								upload.uploading ||
								(needsAttachments && upload.pendingFiles.length === 0)
							}
						>
							{loading || upload.uploading ? 'Submitting…' : action.label}
						</Button>
					</DialogFooter>
				</DialogForm>
			</DialogContent>
		</Dialog>
	);
}
